import { baseApi } from './baseApi';

/** Eine KI-Funktion, wie der ai-service sie für Mandant und Person beurteilt. */
export interface AiFunctionStatus {
  key: string;
  name: string;
  description: string;
  dataClass: 'A' | 'B';
  permission: string;
  enabledForTenant: boolean;
  allowedForUser: boolean;
  /** Alles zusammen: freigeschaltet, berechtigt, Anbieter da, Datenklasse erlaubt. */
  available: boolean;
  reason: string | null;
}

export interface AiTenantSettings {
  enabled: boolean;
  functions: Record<string, boolean>;
  dataClasses: 'A' | 'AB';
  /** Das Modell für Entwürfe — leer: Standard des Dienstes. */
  textModel?: string;
}

export interface AiStatus {
  providerConfigured: boolean;
  /** Das Modell für Vektoren (K02, K25). */
  model: string;
  /** Das Modell für Entwürfe (K01, K18) — das wirksame. */
  textModel: string;
  defaultTextModel: string;
  /** Was der Hub gerade an Textmodellen anbietet. */
  availableTextModels: string[];
  tenant: AiTenantSettings;
  functions: AiFunctionStatus[];
}

/** Die Nutzung je Funktion — Aufrufe aus dem Nachweis, Übernahmen aus den Datensätzen. */
export interface AiUsageFunction {
  key: string;
  name: string;
  art: 'embedding' | 'draft';
  calls: number;
  errors: number;
  avgDurationMs: number | null;
  promptTokens: number;
  completionTokens: number;
  adopted: number | null;
  unchanged: number | null;
  edited: number | null;
}

export interface AiUsage {
  since: string | null;
  functions: AiUsageFunction[];
}

/** Ein Text, den ein Mensch als Muster markiert hat — pseudonymisiert beim Übernehmen. */
export interface StyleExample {
  id: string;
  kind: string;
  sourceEntityType: string | null;
  sourceEntityId: string | null;
  title: string;
  text: string;
  addedBy: string | null;
  createdAt: string;
}

export type DraftFunction = 'finding_draft' | 'measure_status_draft';

export interface DraftRequest {
  function: DraftFunction;
  keywords: string;
  auditField?: string;
  category?: string;
  title?: string;
  description?: string;
  targetStatus?: string;
  recordId?: string;
}

export type DraftSection = 'Sachverhalt' | 'Kriterium' | 'Ursache' | 'Auswirkung' | 'Empfehlung';

/** Ein Entwurf aus dem Modell — gekennzeichnet, mit den Zahlen, die nicht aus der Eingabe stammen. */
export interface DraftResult {
  function: DraftFunction;
  model: string;
  provider: string;
  text: string;
  /** Die fünf Teile einer Feststellung — oder null, wenn das Modell keine Gliederung lieferte. */
  sections: Record<DraftSection, string> | null;
  flaggedNumbers: string[];
  truncated: boolean;
  pseudonymized: boolean;
  replacedNames: number;
  styleExamples: number;
}

export const aiApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAiStatus: builder.query<AiStatus, void>({
      query: () => '/ai/status',
      providesTags: ['Ai'],
    }),
    draft: builder.mutation<DraftResult, DraftRequest>({
      query: (body) => ({ url: '/ai/draft', method: 'POST', body }),
    }),
    getAiUsage: builder.query<AiUsage, void>({
      query: () => '/ai/usage',
      providesTags: ['Ai'],
    }),
    getStyleExamples: builder.query<StyleExample[], void>({
      query: () => '/ai/style-examples',
      providesTags: ['Ai'],
    }),
    addStyleExample: builder.mutation<
      StyleExample,
      { kind: 'finding' | 'report_chapter' | 'minutes'; sourceEntityType: 'finding'; sourceEntityId: string }
    >({
      query: (body) => ({ url: '/ai/style-examples', method: 'POST', body }),
      invalidatesTags: ['Ai'],
    }),
    removeStyleExample: builder.mutation<void, string>({
      query: (id) => ({ url: `/ai/style-examples/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Ai'],
    }),
  }),
});

export const {
  useGetAiStatusQuery,
  useGetAiUsageQuery,
  useDraftMutation,
  useGetStyleExamplesQuery,
  useAddStyleExampleMutation,
  useRemoveStyleExampleMutation,
} = aiApi;
